import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  MapPin, 
  Mail, 
  Leaf, 
  Globe, 
  Network, 
  ArrowRight, 
  Handshake, 
  Rocket, 
  Brain,
  Sprout,
  Heart,
  DollarSign,
  TreePine,
  Phone,
  ExternalLink,
  CheckCircle,
  Zap,
  Award,
  Shield,
  Lightbulb,
  Megaphone,
  ShieldAlert,
  AlertOctagon,
  AlertTriangle,
  CloudRain,
  Scan,
  Smartphone,
  Tractor,
  Factory,
  ChevronRight,
  Video,
  Share2,
  FileText,
  HeartHandshake,
  Sparkles,
  Users,
  Coffee,
  Droplet,
  Recycle,
  HelpCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import AnimatedSection from '../components/AnimatedSection';
import SectionHeader from '../components/shared/SectionHeader';
import StatCard from '../components/shared/StatCard';

const AboutUs: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative min-h-[80vh]">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1625246333195-78d9c38ad449"
            alt="Ethical Agriculture"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#4B0082]/90 via-[#4B0082]/80 to-[#4B0082]/90" />
        </div>
        <div className="relative h-full flex items-center">
          <div className="container mx-auto px-4 py-32">
            <AnimatedSection>
              <div className="max-w-3xl">
                <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-white mb-8">
                  <Building2 className="h-5 w-5" />
                  <span>Our Mission</span>
                </div>
                <h1 className="text-6xl font-bold text-white mb-8 leading-tight">
                  Transforming Agriculture Through <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-white/80">Ethical Innovation</span>
                </h1>
                <p className="text-xl text-white/90 mb-12 leading-relaxed">
                  AVN is dedicated to ending exploitation in global food production through our groundbreaking technology, while promoting sustainable and ethical agricultural practices worldwide.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/signup"
                    className="bg-[#4B0082] text-white px-8 py-4 rounded-lg hover:bg-[#3b0065] transition-colors inline-flex items-center gap-2 text-lg"
                  >
                    <Network className="h-6 w-6" />
                    Join the Movement
                  </Link>
                  <Link
                    to="/dashboard"
                    className="bg-white text-[#4B0082] px-8 py-4 rounded-lg hover:bg-[#4B0082]/10 transition-colors inline-flex items-center gap-2 text-lg"
                  >
                    <ArrowRight className="h-6 w-6" />
                    View Demo
                  </Link>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>

      {/* What Is AVN-Earth AI Section */}
      <div className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="What Is AVN-Earth AI?"
            subtitle="Our AI-powered platform creates transparency and accountability across the entire agricultural supply chain."
            icon={<Brain className="h-5 w-5" />}
            badgeText="Technology"
            badgeColor="purple"
          />

          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <AnimatedSection delay={0.1}>
              <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100 hover:border-[#4B0082]/20 transition-all duration-300">
                <div className="bg-[#4B0082]/10 p-4 rounded-full inline-block mb-6">
                  <Scan className="h-8 w-8 text-[#4B0082]" />
                </div>
                <h3 className="text-2xl font-bold mb-4">AI-Powered Traceability</h3>
                <p className="text-gray-600 mb-6">
                  Our advanced AI system tracks every step of the agricultural process, from seed to shelf, 
                  creating an immutable record of ethical practices and fair compensation.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span>Real-time monitoring of labor practices</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span>Verification of sustainable farming methods</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span>Transparent pricing and value distribution</span>
                  </li>
                </ul>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100 hover:border-[#4B0082]/20 transition-all duration-300">
                <div className="bg-[#4B0082]/10 p-4 rounded-full inline-block mb-6">
                  <Brain className="h-8 w-8 text-[#4B0082]" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Real-Time Analytics</h3>
                <p className="text-gray-600 mb-6">
                  AVN-Earth AI provides actionable insights to farmers, buyers, and consumers, 
                  enabling informed decisions that benefit all stakeholders in the value chain.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span>Market intelligence and price forecasting</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span>Yield optimization recommendations</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span>Supply chain risk assessment</span>
                  </li>
                </ul>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>

      {/* Our Regenerative System Section */}
      <div className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="Our Regenerative System"
            subtitle="AVN combines three powerful pillars to create a holistic approach to agricultural transformation."
            icon={<Leaf className="h-5 w-5" />}
            badgeText="System"
            badgeColor="green"
          />

          <div className="max-w-6xl mx-auto">
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-xl shadow-xl">
                <thead>
                  <tr>
                    <th className="py-4 px-6 bg-[#4B0082] text-white text-left rounded-tl-xl">Pillar</th>
                    <th className="py-4 px-6 bg-[#4B0082] text-white text-left rounded-tr-xl">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="py-4 px-6 font-medium">
                      <div className="flex items-center gap-3">
                        <div className="bg-green-100 p-2 rounded-full">
                          <Sprout className="h-5 w-5 text-green-600" />
                        </div>
                        <span>Regenerative Hybrid Farming</span>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-600">
                      A combination of agroforestry, controlled environment agriculture (CEA), and circular inputs 
                      like black soldier fly compost to maximize yields while restoring soil health and biodiversity.
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-4 px-6 font-medium">
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-100 p-2 rounded-full">
                          <Users className="h-5 w-5 text-blue-600" />
                        </div>
                        <span>Community-Owned Cooperatives</span>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-600">
                      Farmer-led hubs with shared processing facilities and direct export capabilities, 
                      enabling collective bargaining power and eliminating exploitative middlemen.
                    </td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 font-medium">
                      <div className="flex items-center gap-3">
                        <div className="bg-purple-100 p-2 rounded-full">
                          <Brain className="h-5 w-5 text-purple-600" />
                        </div>
                        <span>AVN-Earth AI</span>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-600">
                      Advanced technology that tracks labor practices, inputs, origin, and quality data 
                      across the entire supply chain, ensuring transparency and fair compensation.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Proven Results Section */}
      <div className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="Proven Results"
            subtitle="Our approach delivers measurable impact for farmers, communities, and the environment."
            icon={<CheckCircle className="h-5 w-5" />}
            badgeText="Impact"
            badgeColor="green"
          />

          <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <AnimatedSection delay={0.1}>
              <StatCard 
                value="45%" 
                label="Average Yield Increase" 
                color="green"
              />
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <StatCard 
                value="30%" 
                label="Water Usage Reduction" 
                color="blue"
              />
            </AnimatedSection>
            
            <AnimatedSection delay={0.3}>
              <StatCard 
                value="5,000+" 
                label="Farmers Connected" 
                color="purple"
              />
            </AnimatedSection>
            
            <AnimatedSection delay={0.4}>
              <StatCard 
                value="100%" 
                label="Supply Chain Visibility" 
                color="green"
              />
            </AnimatedSection>
          </div>
        </div>
      </div>

      {/* Unity Bean Coffee Section */}
      <div className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="Unity Bean Coffee"
            subtitle="Our flagship implementation showcases the complete AVN model from farm to cup."
            icon={<Coffee className="h-5 w-5" />}
            badgeText="Case Study"
            badgeColor="amber"
          />

          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-xl shadow-xl overflow-hidden">
              <div className="p-8">
                <h3 className="text-2xl font-bold mb-6 text-center">From Farm to Cup: The Unity Bean Journey</h3>
                
                <div className="relative">
                  {/* Timeline Line */}
                  <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-[#4B0082]/20"></div>
                  
                  {/* Timeline Items */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Item 1 */}
                    <div className="md:col-start-1 relative pb-12">
                      <div className="absolute right-0 md:right-auto md:left-full transform translate-x-1/2 md:-translate-x-1/2 -translate-y-1/4 w-8 h-8 bg-[#4B0082] rounded-full flex items-center justify-center z-10">
                        <Tractor className="h-4 w-4 text-white" />
                      </div>
                      <div className="md:text-right md:pr-12 pl-0">
                        <h4 className="text-lg font-bold mb-2">Farmer</h4>
                        <p className="text-gray-600">
                          273 smallholder farmers in Uganda grow coffee using regenerative practices, 
                          with data captured through the AVN-Earth platform.
                        </p>
                      </div>
                    </div>
                    
                    {/* Item 2 */}
                    <div className="md:col-start-2 relative pb-12">
                      <div className="absolute left-0 md:left-auto md:right-full transform -translate-x-1/2 md:translate-x-1/2 -translate-y-1/4 w-8 h-8 bg-[#4B0082] rounded-full flex items-center justify-center z-10">
                        <Factory className="h-4 w-4 text-white" />
                      </div>
                      <div className="pl-12 md:pl-0">
                        <h4 className="text-lg font-bold mb-2">AVN Hub</h4>
                        <p className="text-gray-600">
                          Edward's Hamlet serves as the processing center, where coffee is processed 
                          and value-added products like coffee cherry syrup are created.
                        </p>
                      </div>
                    </div>
                    
                    {/* Item 3 */}
                    <div className="md:col-start-1 relative pb-12">
                      <div className="absolute right-0 md:right-auto md:left-full transform translate-x-1/2 md:-translate-x-1/2 -translate-y-1/4 w-8 h-8 bg-[#4B0082] rounded-full flex items-center justify-center z-10">
                        <Brain className="h-4 w-4 text-white" />
                      </div>
                      <div className="md:text-right md:pr-12 pl-0">
                        <h4 className="text-lg font-bold mb-2">AVN-Earth AI</h4>
                        <p className="text-gray-600">
                          Every step is verified and tracked, creating a transparent record of ethical 
                          practices and fair compensation throughout the supply chain.
                        </p>
                      </div>
                    </div>
                    
                    {/* Item 4 */}
                    <div className="md:col-start-2 relative pb-12">
                      <div className="absolute left-0 md:left-auto md:right-full transform -translate-x-1/2 md:translate-x-1/2 -translate-y-1/4 w-8 h-8 bg-[#4B0082] rounded-full flex items-center justify-center z-10">
                        <Coffee className="h-4 w-4 text-white" />
                      </div>
                      <div className="pl-12 md:pl-0">
                        <h4 className="text-lg font-bold mb-2">Roastery</h4>
                        <p className="text-gray-600">
                          Coffee is roasted to perfection, maintaining the connection to its origin 
                          and the farmers who grew it through AVN's traceability system.
                        </p>
                      </div>
                    </div>
                    
                    {/* Item 5 */}
                    <div className="md:col-start-1 relative pb-12">
                      <div className="absolute right-0 md:right-auto md:left-full transform translate-x-1/2 md:-translate-x-1/2 -translate-y-1/4 w-8 h-8 bg-[#4B0082] rounded-full flex items-center justify-center z-10">
                        <ShieldAlert className="h-4 w-4 text-white" />
                      </div>
                      <div className="md:text-right md:pr-12 pl-0">
                        <h4 className="text-lg font-bold mb-2">Ethical Retail</h4>
                        <p className="text-gray-600">
                          Retailers can verify and showcase the ethical journey of Unity Bean, 
                          providing consumers with transparent information about their purchase.
                        </p>
                      </div>
                    </div>
                    
                    {/* Item 6 */}
                    <div className="md:col-start-2 relative">
                      <div className="absolute left-0 md:left-auto md:right-full transform -translate-x-1/2 md:translate-x-1/2 -translate-y-1/4 w-8 h-8 bg-[#4B0082] rounded-full flex items-center justify-center z-10">
                        <Heart className="h-4 w-4 text-white" />
                      </div>
                      <div className="pl-12 md:pl-0">
                        <h4 className="text-lg font-bold mb-2">Customer</h4>
                        <p className="text-gray-600">
                          Consumers enjoy premium coffee while supporting a system that ensures 
                          farmers receive at least 50% of the final value, compared to just 6% in traditional models.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-12 text-center">
                  <Link
                    to="/unity-bean"
                    className="inline-flex items-center gap-2 bg-[#4B0082] text-white px-6 py-3 rounded-lg hover:bg-[#3b0065] transition-colors"
                  >
                    <Coffee className="h-5 w-5" />
                    <span>Learn More About Unity Bean</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Our SDG Commitments Section */}
      <div className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="Our SDG Commitments"
            subtitle="AVN's work directly contributes to seven of the UN Sustainable Development Goals."
            icon={<Globe className="h-5 w-5" />}
            badgeText="Impact"
            badgeColor="blue"
          />

          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <AnimatedSection delay={0.1}>
              <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-red-200">
                <div className="bg-red-100 p-3 rounded-full inline-block mb-4">
                  <DollarSign className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-bold mb-2">No Poverty</h3>
                <p className="text-gray-600">
                  By ensuring farmers receive at least 50% of the final value of their products, 
                  AVN helps lift agricultural communities out of poverty.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.15}>
              <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-yellow-200">
                <div className="bg-yellow-100 p-3 rounded-full inline-block mb-4">
                  <Sprout className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="text-lg font-bold mb-2">Zero Hunger</h3>
                <p className="text-gray-600">
                  Our regenerative farming practices increase yields by an average of 45%, 
                  helping to ensure food security for farming communities.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-blue-200">
                <div className="bg-blue-100 p-3 rounded-full inline-block mb-4">
                  <Droplet className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold mb-2">Clean Water</h3>
                <p className="text-gray-600">
                  AVN's water conservation techniques reduce agricultural water usage by 30%, 
                  preserving this precious resource for communities.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.25}>
              <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-purple-200">
                <div className="bg-purple-100 p-3 rounded-full inline-block mb-4">
                  <Handshake className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-bold mb-2">Decent Work</h3>
                <p className="text-gray-600">
                  Our AI-powered verification system ensures fair labor practices and 
                  eliminates exploitation throughout the supply chain.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.3}>
              <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-green-200">
                <div className="bg-green-100 p-3 rounded-full inline-block mb-4">
                  <CloudRain className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-lg font-bold mb-2">Climate Action</h3>
                <p className="text-gray-600">
                  Regenerative farming practices sequester carbon, reduce emissions, 
                  and build resilience to climate change impacts.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.35}>
              <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-amber-200">
                <div className="bg-amber-100 p-3 rounded-full inline-block mb-4">
                  <Recycle className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-bold mb-2">Responsible Consumption</h3>
                <p className="text-gray-600">
                  AVN's transparency tools enable consumers to make informed choices 
                  about the ethical and environmental impact of their purchases.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.4}>
              <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-blue-200">
                <div className="bg-blue-100 p-3 rounded-full inline-block mb-4">
                  <Shield className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold mb-2">Peace & Strong Institutions</h3>
                <p className="text-gray-600">
                  By creating transparent, fair systems, AVN helps build trust and 
                  reduce conflicts related to resource distribution.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>

      {/* FAQs Section */}
      <div className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="Frequently Asked Questions"
            subtitle="Find answers to common questions about AVN and our approach."
            icon={<HelpCircle className="h-5 w-5" />}
            badgeText="FAQs"
          />

          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              <AnimatedSection delay={0.1}>
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <details className="group">
                    <summary className="flex items-center justify-between cursor-pointer p-6">
                      <h3 className="text-lg font-semibold">How does AVN-Earth AI verify ethical practices?</h3>
                      <div className="transition-transform duration-300 group-open:rotate-180">
                        <ChevronRight className="h-5 w-5" />
                      </div>
                    </summary>
                    <div className="px-6 pb-6 text-gray-600">
                      <p>
                        AVN-Earth AI uses a multi-layered verification approach that combines:
                      </p>
                      <ul className="list-disc pl-5 mt-2 space-y-1">
                        <li>On-the-ground data collection by trusted community members</li>
                        <li>IoT sensors and satellite imagery for environmental monitoring</li>
                        <li>Blockchain technology to create immutable records</li>
                        <li>AI analysis to detect anomalies and verify claims</li>
                        <li>Third-party audits to validate the system</li>
                      </ul>
                      <p className="mt-2">
                        This comprehensive approach ensures that ethical claims are backed by verifiable data and real-world checks.
                      </p>
                    </div>
                  </details>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.2}>
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <details className="group">
                    <summary className="flex items-center justify-between cursor-pointer p-6">
                      <h3 className="text-lg font-semibold">How is value distributed in the AVN model?</h3>
                      <div className="transition-transform duration-300 group-open:rotate-180">
                        <ChevronRight className="h-5 w-5" />
                      </div>
                    </summary>
                    <div className="px-6 pb-6 text-gray-600">
                      <p>
                        AVN ensures that farmers receive at least 50% of the final value of their products, compared to the 6% they typically receive in traditional models. The value distribution is as follows:
                      </p>
                      <ul className="list-disc pl-5 mt-2 space-y-1">
                        <li><strong>Farmers:</strong> Minimum 50% (up to 70% in many cases)</li>
                        <li><strong>Processing & Logistics:</strong> 15-20%</li>
                        <li><strong>AVN Platform & Technology:</strong> 10-15%</li>
                        <li><strong>Retail & Distribution:</strong> 10-15%</li>
                      </ul>
                      <p className="mt-2">
                        This distribution is transparently tracked through our blockchain-based system, ensuring accountability at every step.
                      </p>
                    </div>
                  </details>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.3}>
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <details className="group">
                    <summary className="flex items-center justify-between cursor-pointer p-6">
                      <h3 className="text-lg font-semibold">How can brands work with AVN?</h3>
                      <div className="transition-transform duration-300 group-open:rotate-180">
                        <ChevronRight className="h-5 w-5" />
                      </div>
                    </summary>
                    <div className="px-6 pb-6 text-gray-600">
                      <p>
                        Brands can partner with AVN in several ways:
                      </p>
                      <ul className="list-disc pl-5 mt-2 space-y-1">
                        <li><strong>Sourcing:</strong> Access ethically verified ingredients directly from AVN's network of farmer cooperatives</li>
                        <li><strong>Verification:</strong> Implement AVN-Earth AI in your existing supply chains to verify ethical practices</li>
                        <li><strong>Co-branding:</strong> Develop co-branded products with AVN's ethical certification</li>
                        <li><strong>Technology Licensing:</strong> License AVN's technology for your own supply chain management</li>
                      </ul>
                      <p className="mt-2">
                        Contact our partnership team at <a href="mailto:partnerships@avnearth.com" className="text-[#4B0082] hover:underline">partnerships@avnearth.com</a> to discuss collaboration opportunities.
                      </p>
                    </div>
                  </details>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.4}>
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <details className="group">
                    <summary className="flex items-center justify-between cursor-pointer p-6">
                      <h3 className="text-lg font-semibold">What types of farmers can join the AVN network?</h3>
                      <div className="transition-transform duration-300 group-open:rotate-180">
                        <ChevronRight className="h-5 w-5" />
                      </div>
                    </summary>
                    <div className="px-6 pb-6 text-gray-600">
                      <p>
                        AVN works with a diverse range of agricultural producers:
                      </p>
                      <ul className="list-disc pl-5 mt-2 space-y-1">
                        <li>Smallholder farmers (0.5-5 acres)</li>
                        <li>Medium-sized farms (5-50 acres)</li>
                        <li>Farmer cooperatives and associations</li>
                        <li>Indigenous farming communities</li>
                        <li>Urban and peri-urban farmers</li>
                      </ul>
                      <p className="mt-2">
                        We prioritize farmers who are committed to adopting regenerative practices and participating in our cooperative model. Our technology is designed to be accessible even in areas with limited connectivity.
                      </p>
                    </div>
                  </details>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.5}>
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <details className="group">
                    <summary className="flex items-center justify-between cursor-pointer p-6">
                      <h3 className="text-lg font-semibold">How does AVN ensure data privacy and security?</h3>
                      <div className="transition-transform duration-300 group-open:rotate-180">
                        <ChevronRight className="h-5 w-5" />
                      </div>
                    </summary>
                    <div className="px-6 pb-6 text-gray-600">
                      <p>
                        AVN takes data privacy and security seriously:
                      </p>
                      <ul className="list-disc pl-5 mt-2 space-y-1">
                        <li>Farmers maintain ownership of their data</li>
                        <li>End-to-end encryption protects sensitive information</li>
                        <li>Decentralized storage minimizes vulnerability to breaches</li>
                        <li>Transparent data usage policies with explicit consent</li>
                        <li>Regular security audits and compliance with global standards</li>
                      </ul>
                      <p className="mt-2">
                        Our approach ensures that data is used ethically and benefits those who generate it, rather than being extracted without compensation.
                      </p>
                    </div>
                  </details>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="Get in Touch"
            subtitle="Have questions or want to learn more about AVN? We'd love to hear from you."
            icon={<Mail className="h-5 w-5" />}
            badgeText="Contact"
          />

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              <AnimatedSection>
                <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100">
                  <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="bg-[#4B0082]/10 p-2 rounded-full mt-1">
                        <Mail className="h-5 w-5 text-[#4B0082]" />
                      </div>
                      <div>
                        <h4 className="font-medium">Email</h4>
                        <a href="mailto:info@avnearth.com" className="text-[#4B0082] hover:underline">info@avnearth.com</a>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="bg-[#4B0082]/10 p-2 rounded-full mt-1">
                        <Phone className="h-5 w-5 text-[#4B0082]" />
                      </div>
                      <div>
                        <h4 className="font-medium">Phone</h4>
                        <a href="tel:+447949500433" className="text-[#4B0082] hover:underline">+44 794 950 0433</a>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="bg-[#4B0082]/10 p-2 rounded-full mt-1">
                        <MapPin className="h-5 w-5 text-[#4B0082]" />
                      </div>
                      <div>
                        <h4 className="font-medium">Address</h4>
                        <p>128 City Road, London, EC1V 2NX, United Kingdom</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="bg-[#4B0082]/10 p-2 rounded-full mt-1">
                        <Globe className="h-5 w-5 text-[#4B0082]" />
                      </div>
                      <div>
                        <h4 className="font-medium">Website</h4>
                        <a href="https://www.avnearth.com" target="_blank" rel="noopener noreferrer" className="text-[#4B0082] hover:underline flex items-center gap-1">
                          www.avnearth.com
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.2}>
                <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100">
                  <h3 className="text-2xl font-bold mb-6">Send Us a Message</h3>
                  
                  <form className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Your Name
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4B0082] focus:border-[#4B0082]"
                        placeholder="Enter your name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4B0082] focus:border-[#4B0082]"
                        placeholder="Enter your email"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Subject
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4B0082] focus:border-[#4B0082]"
                        placeholder="Enter subject"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Message
                      </label>
                      <textarea
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4B0082] focus:border-[#4B0082]"
                        placeholder="Enter your message"
                      ></textarea>
                    </div>
                    
                    <button
                      type="submit"
                      className="w-full bg-[#4B0082] text-white py-3 px-6 rounded-lg hover:bg-[#3b0065] transition-colors"
                    >
                      Send Message
                    </button>
                  </form>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;